/**
 * Resolves a persisted shapeId to a geometry preset.
 *
 * Shape type is encoded in the id as `${type}shape${uuid}` and that format is
 * fixed by the backend, so this module is the single place that knows how to
 * read it. It must never return null: an unrecognised token still renders as a
 * rectangle rather than leaving a blank box on someone's slide.
 */
import { PRESETS } from './presets';
import { clamp } from './primitives';

const FALLBACK = PRESETS.rect;

/** token -> preset name, built from each preset's declared legacy tokens. */
const TOKEN_MAP = Object.values(PRESETS).reduce((map, preset) => {
  preset.legacyTokens.forEach((token) => {
    map[token.toLowerCase()] = preset.name;
  });
  return map;
}, {});

export const SHAPE_GROUPS = ['Rectangles', 'Basic Shapes'];

/** The entries the shape picker offers, in PowerPoint's grouping order. */
export const SHAPE_CATALOG = [
  { token: 'rectangle', presetName: 'rect', label: 'Rectangle', group: 'Rectangles' },
  { token: 'square', presetName: 'rect', label: 'Square', group: 'Rectangles' },
  { token: 'round', presetName: 'roundRect', label: 'Rounded Rectangle', group: 'Rectangles' },
  { token: 'single', presetName: 'snipRect', label: 'Snip Single Corner Rectangle', group: 'Rectangles' },

  { token: 'circle', presetName: 'ellipse', label: 'Circle', group: 'Basic Shapes' },
  { token: 'oval', presetName: 'ellipse', label: 'Oval', group: 'Basic Shapes' },
  { token: 'triangle', presetName: 'triangle', label: 'Isosceles Triangle', group: 'Basic Shapes' },
  { token: 'rightTriangle', presetName: 'rightTriangle', label: 'Right Triangle', group: 'Basic Shapes' },
  { token: 'parallelogram', presetName: 'parallelogram', label: 'Parallelogram', group: 'Basic Shapes' },
  { token: 'trapezoid', presetName: 'trapezoid', label: 'Trapezoid', group: 'Basic Shapes' },
  { token: 'diamond', presetName: 'diamond', label: 'Diamond', group: 'Basic Shapes' },
  { token: 'pentagon', presetName: 'pentagon', label: 'Regular Pentagon', group: 'Basic Shapes' },
  { token: 'hexagon', presetName: 'hexagon', label: 'Hexagon', group: 'Basic Shapes' },
  { token: 'heptagon', presetName: 'heptagon', label: 'Heptagon', group: 'Basic Shapes' },
  { token: 'octagon', presetName: 'octagon', label: 'Octagon', group: 'Basic Shapes' },
  { token: 'decagon', presetName: 'decagon', label: 'Decagon', group: 'Basic Shapes' },
  { token: 'dodecagon', presetName: 'dodecagon', label: 'Dodecagon', group: 'Basic Shapes' },
  { token: 'pie', presetName: 'pie', label: 'Pie', group: 'Basic Shapes' },
  { token: 'chord', presetName: 'chord', label: 'Chord', group: 'Basic Shapes' },
  { token: 'teardrop', presetName: 'teardrop', label: 'Teardrop', group: 'Basic Shapes' },
  { token: 'frame', presetName: 'frame', label: 'Frame', group: 'Basic Shapes' },
  { token: 'halfFrame', presetName: 'halfFrame', label: 'Half Frame', group: 'Basic Shapes' },
  { token: 'corner', presetName: 'corner', label: 'L-Shape', group: 'Basic Shapes' },
  { token: 'diagStripe', presetName: 'diagStripe', label: 'Diagonal Stripe', group: 'Basic Shapes' },
  { token: 'plus', presetName: 'plus', label: 'Cross', group: 'Basic Shapes' },
  { token: 'plaque', presetName: 'plaque', label: 'Plaque', group: 'Basic Shapes' },
  { token: 'cylinder', presetName: 'cylinder', label: 'Cylinder', group: 'Basic Shapes' },
  { token: 'cube', presetName: 'cube', label: 'Cube', group: 'Basic Shapes' },
  { token: 'bevel', presetName: 'bevel', label: 'Bevel', group: 'Basic Shapes' },
  { token: 'donut', presetName: 'donut', label: 'Donut', group: 'Basic Shapes' },
  { token: 'noSmoking', presetName: 'noSmoking', label: '"No" Symbol', group: 'Basic Shapes' },
  { token: 'blockArc', presetName: 'blockArc', label: 'Block Arc', group: 'Basic Shapes' },
  { token: 'foldedCorner', presetName: 'foldedCorner', label: 'Folded Corner', group: 'Basic Shapes' },
  { token: 'smileyFace', presetName: 'smileyFace', label: 'Smiley Face', group: 'Basic Shapes' },
  { token: 'star', presetName: 'star5', label: '5-Point Star', group: 'Basic Shapes' },
  { token: 'heart', presetName: 'heart', label: 'Heart', group: 'Basic Shapes' },
  { token: 'lightningBolt', presetName: 'lightningBolt', label: 'Lightning Bolt', group: 'Basic Shapes' },
  { token: 'sun', presetName: 'sun', label: 'Sun', group: 'Basic Shapes' },
  { token: 'moon', presetName: 'moon', label: 'Moon', group: 'Basic Shapes' },
  { token: 'cloud', presetName: 'cloud', label: 'Cloud', group: 'Basic Shapes' },
  { token: 'arc', presetName: 'arc', label: 'Arc', group: 'Basic Shapes' },
  { token: 'callout', presetName: 'wedgeRectCallout', label: 'Rectangular Callout', group: 'Basic Shapes' },
];

/**
 * The pre-rebuild behaviour: substring matching on 5-char prefixes. Kept only as
 * a safety net for ids this module's token table does not recognise.
 */
const legacyFallback = (shapeId) => {
  const lower = shapeId.toLowerCase();
  const found = Object.keys(TOKEN_MAP).find((token) => lower.includes(token));
  return found ? PRESETS[TOKEN_MAP[found]] : FALLBACK;
};

export const resolvePreset = (shapeId) => {
  if (typeof shapeId !== 'string' || shapeId.length === 0) return FALLBACK;

  const match = /^([a-zA-Z]+)shape/.exec(shapeId);
  if (match) {
    const presetName = TOKEN_MAP[match[1].toLowerCase()];
    if (presetName) return PRESETS[presetName];
  }

  return legacyFallback(shapeId);
};

/** Merge stored adjust values over preset defaults, clamped to each range. */
export const resolveAdjustments = (preset, stored) =>
  preset.adjustments.reduce((acc, spec) => {
    const raw = stored?.[spec.name];
    const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : spec.default;
    return { ...acc, [spec.name]: clamp(value, spec.min, spec.max) };
  }, {});
